import React from "react";
import { useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import Form, {
  SimpleItem,
  GroupItem,
  RequiredRule,
} from "devextreme-react/form";
import "devextreme-react/tag-box";
import { selectStoreRegions } from "../../redux/cash-in-out/cash-in-out-selectors";
import {
  selectCategories,
  selectBrands,
  selectGroups,
  selectSuppliers,
} from "../../redux/general/selector";
import ListLayout from "../common/ListLayout";
import { List, TextBox, Button, CheckBox } from "devextreme-react";
import HelpToolTip from "../common/HelpToolTip";
import { useAuth } from "../../context/authContext";
import { getDefaultLang } from "../../utils";

const FilterSelector = ({
  formData,
  handleSubmit,
  isSelected,
  isEmpty,
  searchString,
  onValueChanged,
  onItemDeleted,
  onSelectedItemsChange,
  selectedItems,
  productsList,
  autoCreateAmount,
  open,
  setOpen,
  newFilters,
  setNewFilters,
  getStoreRegionWarehouses,
}) => {
  const { brands, categories, groups, suppliers, storeRegions } = useSelector(
    createStructuredSelector({
      brands: selectBrands,
      categories: selectCategories,
      groups: selectGroups,
      suppliers: selectSuppliers,
      storeRegions: selectStoreRegions,
    })
  );

  const { activeLanguages, configParams, orderedWarehouses } = useAuth();

  const defaultAccountLang = getDefaultLang(
    configParams.default_language,
    activeLanguages
  );

  const onFieldDataChanged = (e) => {
    if (e.dataField === "storeRegion" && e.value) {
      getStoreRegionWarehouses(e.value);
    }
  };

  return (
    <div className="mt-4">
      <div
        onClick={() => setOpen(!open)}
        className={
          open
            ? "d-flex align-items-center justify-content-between rounded-top open pr-4 pl-4"
            : "d-flex align-items-center justify-content-between border rounded p-1  pr-4 pl-4"
        }
        style={{ cursor: "pointer" }}
      >
        <h4>Filters</h4>

        {open ? (
          <i className="fas fa-chevron-up"></i>
        ) : (
          <i class="fas fa-chevron-down"></i>
        )}
      </div>
      <div className={open ? "collapse show" : "collapse"} id="collapseExample">
        <div
          className={
            open ? "card card-body rounded-bottom" : "card card-body rounded-0"
          }
          style={open ? { borderTop: "0px" } : {}}
        >
          <div className="">
            <form onSubmit={handleSubmit}>
              <Form formData={formData} onFieldDataChanged={onFieldDataChanged}>
                <GroupItem colCount={3}>
                  <SimpleItem
                    dataField="sourceLocation"
                    editorType="dxSelectBox"
                    editorOptions={{
                      showClearButton: true,
                      dataSource: orderedWarehouses,
                      displayExpr: defaultAccountLang,
                      valueExpr: "id",
                      placeholder: "Select source location",
                      searchEnabled: true,
                    }}
                  >
                    <RequiredRule message="Source location is required" />
                  </SimpleItem>
                  <SimpleItem
                    dataField="destinationLocation"
                    editorType="dxTagBox"
                    editorOptions={{
                      showClearButton: true,
                      dataSource: orderedWarehouses,
                      displayExpr: defaultAccountLang,
                      valueExpr: "id",
                      placeholder: "Select distination location",
                      searchEnabled: true,
                      showDropDownButton: true,
                      showSelectionControls: true,
                    }}
                  >
                    <RequiredRule message="Distination location is required" />
                  </SimpleItem>
                  <SimpleItem
                    dataField="storeRegion"
                    editorType="dxSelectBox"
                    editorOptions={{
                      showClearButton: true,
                      dataSource: storeRegions,
                      displayExpr: "name",
                      valueExpr: "storeRegionID",
                      searchEnabled: true,
                      placeholder: "Select store region",
                    }}
                  />
                </GroupItem>
                <GroupItem colCount={3}>
                  <SimpleItem
                    dataField="productCategories"
                    editorType="dxTagBox"
                    editorOptions={{
                      showClearButton: true,
                      dataSource: categories,
                      displayExpr: "name",
                      valueExpr: "id",
                      placeholder: "Select product category",
                      searchEnabled: true,
                      showDropDownButton: true,
                      showSelectionControls: true,
                    }}
                  />
                  <SimpleItem
                    dataField="brands"
                    editorType="dxTagBox"
                    editorOptions={{
                      showClearButton: true,
                      dataSource: brands,
                      displayExpr: "name",
                      valueExpr: "brandID",
                      placeholder: "Select brand",
                      searchEnabled: true,
                      showDropDownButton: true,
                      showSelectionControls: true,
                    }}
                  />
                  <SimpleItem
                    dataField="suppliers"
                    editorType="dxTagBox"
                    editorOptions={{
                      showClearButton: true,
                      dataSource: suppliers,
                      displayExpr: "companyName",
                      valueExpr: "id",
                      placeholder: "Select supplier",
                      searchEnabled: true,
                      showDropDownButton: true,
                    }}
                  />
                </GroupItem>

                <GroupItem colCount={4}>
                  <SimpleItem
                    dataField="productGroups"
                    editorType="dxTagBox"
                    editorOptions={{
                      showClearButton: true,
                      dataSource: groups,
                      displayExpr: "name",
                      valueExpr: "id",
                      placeholder: "Select product group",
                      searchEnabled: true,
                      showDropDownButton: true,
                      showSelectionControls: true,
                    }}
                  />
                  <SimpleItem
                    dataField="reportType"
                    editorType="dxSelectBox"
                    label={{ text: "Type of documents created by the report" }}
                    editorOptions={{
                      showClearButton: true,
                      dataSource: [
                        { display: "Transfer order", value: "transferOrder" },
                        {
                          display: "Inventory transfer",
                          value: "inventoryTransfer",
                        },
                      ],
                      displayExpr: "display",
                      valueExpr: "value",
                      placeholder: "Select document type",
                      searchEnabled: false,
                      showDropDownButton: true,
                    }}
                  >
                    <RequiredRule message="Type of documents created by the report is required" />
                  </SimpleItem>
                  <SimpleItem
                    dataField="orderBy"
                    editorType="dxSelectBox"
                    editorOptions={{
                      searchEnabled: false,
                      showDropDownButton: true,
                      displayExpr: "display",
                      valueExpr: "value",
                      dataSource: [
                        { value: "PRODUCT_NAME", display: "Product name" },
                        { value: "PRODUCT_ID", display: "Product ID" },
                        { value: "PRODUCT_CODE", display: "Product code" },
                      ],
                    }}
                  />
                  <SimpleItem
                    dataField="orderDirection"
                    editorType="dxSelectBox"
                    editorOptions={{
                      searchEnabled: false,
                      showDropDownButton: true,
                      dataSource: ["ASC", "DESC"],
                    }}
                  />
                </GroupItem>
              </Form>
              <div
                style={{
                  width: "100%",
                  marginBottom: "30px",
                }}
              >
                <div
                  style={{
                    paddingTop: "10px",
                    width: "65.5%",
                  }}
                >
                  <span className="sub-title">Products</span>

                  <TextBox
                    placeholder="Add products"
                    showClearButton={true}
                    value={searchString}
                    valueChangeEvent="keyup"
                    width="100%"
                    onValueChanged={(event) =>
                      onValueChanged(event, "searchString")
                    }
                  >
                    {productsList.length && !isSelected ? (
                      <ListLayout
                        lists={productsList}
                        displayName="formatted"
                        onSelectedItemsChange={onSelectedItemsChange}
                      />
                    ) : null}
                  </TextBox>

                  {selectedItems.length && !isEmpty ? (
                    <React.Fragment>
                      <h4 style={{ marginTop: "10px" }}>
                        Selected Products {selectedItems.length}
                      </h4>
                      <List
                        dataSource={selectedItems}
                        displayExpr="formatted"
                        showSelectionControls={true}
                        searchMode="contains"
                        searchExpr="formatted"
                        searchEnabled={selectedItems.length >= 7}
                        height={selectedItems.length >= 7 ? 250 : "auto"}
                        selectionMode="none"
                        allowItemDeleting={true}
                        itemDeleteMode="static"
                        onItemDeleted={onItemDeleted}
                      ></List>
                    </React.Fragment>
                  ) : null}
                </div>

                <div
                  className="d-flex align-items-center"
                  style={{
                    paddingTop: "25px",
                  }}
                >
                  <div className="d-flex align-items-center mr-4">
                    <CheckBox
                      text="Automatically share limited supply between destinations"
                      value={autoCreateAmount}
                      onValueChanged={(event) =>
                        onValueChanged(event, "autoCreateAmount")
                      }
                    />
                    <div
                      style={{
                        paddingTop: "3px",
                      }}
                    >
                      {" "}
                      <HelpToolTip
                        id="autoFill"
                        message="When autofill is used then the server will automatically try to fill the required amounts (based on available stock) between the warehouses as equally as possible. This can be used when the available stock is not enough to satisfy all the requirements."
                      />
                    </div>
                  </div>
                  <div className="d-flex align-items-center mr-4">
                    <CheckBox
                      text="Only list destinations with a Reorder Point"
                      value={newFilters.toLocationsWithReorderPointOnly}
                      onValueChanged={(event) =>
                        setNewFilters({
                          ...newFilters,
                          toLocationsWithReorderPointOnly: event.value,
                        })
                      }
                    />
                    <div
                      style={{
                        paddingTop: "3px",
                      }}
                    >
                      {" "}
                      <HelpToolTip
                        id="toLocationsWithReorderPointOnly"
                        message="When checked, the report will only list destination locations that have a Reorder Point for the particular product. Other destinations will not be listed, even if that location has open orders for that product."
                      />
                    </div>
                  </div>
                  <div className="d-flex align-items-center mr-4">
                    <CheckBox
                      text="Ignore location assortments"
                      value={newFilters.ignoreAssortments}
                      onValueChanged={(event) =>
                        setNewFilters({
                          ...newFilters,
                          ignoreAssortments: event.value,
                        })
                      }
                    />
                    <div
                      style={{
                        paddingTop: "3px",
                      }}
                    >
                      {" "}
                      <HelpToolTip
                        id="ignoreAssortments"
                        message="If there is an assortment defined for a destination location, the report will list only assortment products and ignore others, even if those products have a Reorder Point or open sales orders. Check this box to turn the restriction off."
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center mr-4 mt-4">
                  <CheckBox
                    text="Suggestions must follow Reorder Multiples"
                    value={newFilters.useReorderMultiplesInSuggestedAmounts}
                    onValueChanged={(event) =>
                      setNewFilters({
                        ...newFilters,
                        useReorderMultiplesInSuggestedAmounts: event.value,
                      })
                    }
                  />
                  <div
                    style={{
                      paddingTop: "3px",
                    }}
                  >
                    {" "}
                    <HelpToolTip
                      id="useReorderMultiplesInSuggestedAmounts"
                      message="When enabled then suggested transfer amounts will be based on the products reorder only in multiples values (when it is set). This means that if a location requires 3 items but the multiple value has been set to 5, then the suggested amount will be set to 5 provided that there is enough stock available. Similarly if the there is less than the set multiple value of items available then the suggested amount will be 0."
                    />
                  </div>
                </div>
              </div>
              <Button
                useSubmitBehavior={true}
                stylingMode="contained"
                type="default"
                text="Get report"
                elementAttr={{ style: "background-color: #0737a0" }}
              />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterSelector;
